import { ReactNode, useCallback, useEffect, useState } from 'react';

import { NO_CHIP_MINIMIZER } from '~/core/constants';
import { ProductType } from '~/core/enums';
import useResetableState from '~/core/hooks/useResetableState';
import {
  ProductDetails,
  ProductQty,
  SelectedItemMap,
  SelectedItemProperties,
} from '~/core/models';
import { ChipMinimizerModel } from '~/core/models/chip-minimizer.model';
import { buildQteAttributes, isActualProduct } from '~/core/utils';
import { createTypeSafeContext } from '~/core/utils';
import { productQtyFromSelectedItems } from '~/core/utils/productQtyFromSelectedItems';

export interface ChipMinimizerContextValue {
  chipMinimizer?: ChipMinimizerModel;
  setChipMinimizer: (value?: ChipMinimizerModel) => void;
  selectedChipMinimizers: SelectedItemMap;
  setSelectedChipMinimizers: (value: SelectedItemMap) => void;
  resetSelectedChipMinimizers: () => void;
  restorableChipMinimizers: SelectedItemMap;
  setRestorableChipMinimizers: (value: SelectedItemMap) => void;
  makeRestorableChipMinimizers: () => SelectedItemMap;
  resetRestorableChipMinimizers: () => void;
  chipMinimizerToProductDetails: () => ProductDetails[];
  getChipMinimizersProductQty: () => ProductQty[];
}

export const [useChipMinimizerContext, ChipMinimizerContext] =
  createTypeSafeContext<ChipMinimizerContextValue>();

export const ChipMinimizerProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [chipMinimizer, setChipMinimizer] = useState<ChipMinimizerModel>();
  const [
    selectedChipMinimizers,
    setSelectedChipMinimizers,
    resetSelectedChipMinimizers,
  ] = useResetableState<Record<number, SelectedItemProperties>>({});
  const [
    restorableChipMinimizers,
    setRestorableChipMinimizers,
    resetRestorableChipMinimizers,
  ] = useResetableState<Record<number, SelectedItemProperties>>({});

  const chipMinimizerToProductDetails = (): ProductDetails[] => {
    if (!chipMinimizer) {
      return [];
    }
    const detail: ProductDetails = {
      productId: chipMinimizer.productId,
      name: chipMinimizer.name,
      consumerDesc: chipMinimizer.consumerDesc,
      abbrevDesc: chipMinimizer.abbrevDesc,
      productType: ProductType.LABOR,
      price: chipMinimizer.price,
      priceWithoutDiscount: chipMinimizer.price,
      unitOfMeasure: chipMinimizer.unitOfMeasure,
      ...buildQteAttributes(chipMinimizer),
    };

    return [detail];
  };

  const getChipMinimizersProductQty = (): ProductQty[] => {
    const emptyArray = [] satisfies ProductQty[];

    // validates that there's still a chip-minimizer available first
    // this should be unnecessary if resetSelectedChipMinimizers is called properly, but it wasn't
    return isActualProduct(chipMinimizer?.productId ?? 0)
      ? productQtyFromSelectedItems(selectedChipMinimizers)
      : emptyArray;
  };

  const makeRestorableChipMinimizers = (): Record<
    number,
    SelectedItemProperties
  > =>
    Object.entries(selectedChipMinimizers).reduce<
      Record<number, SelectedItemProperties>
    >((acc, [id, itemProps]) => {
      const chipMinimizerId = parseInt(id);
      const isNoChipMinimizerItem =
        chipMinimizerId === NO_CHIP_MINIMIZER.productId;

      const isSelectedWithQuantity = !!(
        itemProps.isSelected && itemProps.quantity
      );

      if (isNoChipMinimizerItem || isSelectedWithQuantity) {
        acc[chipMinimizerId] = itemProps;
      }

      return acc;
    }, {});

  const restoreSelectedChipMinimizers = useCallback(() => {
    if (chipMinimizer && Object.keys(restorableChipMinimizers).length > 0) {
      const selectedMap: Record<number, SelectedItemProperties> = {};
      const restorableChipMinimizer =
        restorableChipMinimizers[chipMinimizer.productId];

      if (restorableChipMinimizer) {
        selectedMap[chipMinimizer.productId] = restorableChipMinimizer;
        setSelectedChipMinimizers(selectedMap);
      }
      resetRestorableChipMinimizers();
    }
  }, [
    chipMinimizer,
    restorableChipMinimizers,
    setSelectedChipMinimizers,
    resetRestorableChipMinimizers,
  ]);

  useEffect(() => {
    restoreSelectedChipMinimizers();
  }, [chipMinimizer, restoreSelectedChipMinimizers]);

  const chipMinimizerContextValue: ChipMinimizerContextValue = {
    chipMinimizer,
    setChipMinimizer,
    selectedChipMinimizers,
    setSelectedChipMinimizers,
    resetSelectedChipMinimizers,
    restorableChipMinimizers,
    setRestorableChipMinimizers,
    makeRestorableChipMinimizers,
    resetRestorableChipMinimizers,
    chipMinimizerToProductDetails,
    getChipMinimizersProductQty,
  };

  return (
    <ChipMinimizerContext.Provider value={chipMinimizerContextValue}>
      {children}
    </ChipMinimizerContext.Provider>
  );
};
